import axios from 'axios';
import constants from '~/utils/constants';
import generalUtils from '~/utils/general-utils';
import type {
    AddBreakArgs,
    AxiosApiResponse,
    DeleteBreakArgs,
    EditBreakArgs
} from '../types';
import type {
    AddBreakResponse,
    AddNewTripToRouteArgs,
    AugmentRouteArgs,
    AugmentRouteResponse,
    BulkUnassignTasksArgs,
    DeleteBreakResponse,
    EditBreakResponse,
    MoveRouteArgs,
    OptimizeRoutesArgs,
    OptimizeRoutesResponse,
    ReassignTasksArgs,
    ReassignTasksResponse,
    RepairPlanArgs,
    RepairPlanResponse,
    ResequenceStopArgs,
    ResequenceTasksArgs,
    ResequenceTasksResponse,
    SinglePlanEditArgs,
    UnassignTasksArgs,
    UnassignTasksResponse
} from './types';

/**
 * Implementations of API methods under the `/schedules/evaluate` path
 *
 * @category API
 */
export default class SinglePlanEditApi {
    /**
     * Path of the single plan edit endpoint
     */
    private static readonly path = '/schedules/evaluate?action=';

    static unassignTasks(
        clientId: string,
        { date, routeId, taskIds }: UnassignTasksArgs
    ): Promise<UnassignTasksResponse> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!date) {
            return Promise.reject('Missing date');
        }
        if (!routeId) {
            return Promise.reject('Missing routeId');
        }
        if (!taskIds || !Array.isArray(taskIds) || !taskIds.length) {
            return Promise.reject('Missing taskIds');
        }

        const params: SinglePlanEditArgs = {
            date,
            routeId,
            taskIds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}trim&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static bulkUnassignTasks({
        date,
        taskIdsByRouteId,
        taskIds
    }: BulkUnassignTasksArgs): Promise<UnassignTasksResponse> {
        if (!date) {
            return Promise.reject('Missing date');
        }
        if (!taskIdsByRouteId) {
            return Promise.reject('Missing taskIdsByRouteId');
        }
        if (!taskIds || !Array.isArray(taskIds) || !taskIds.length) {
            return Promise.reject('Missing taskIds');
        }

        const params: SinglePlanEditArgs = {
            date,
            taskIdsByRouteId,
            taskIds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}trim&accept`, params);
    }

    static augmentRoute(
        clientId: string,
        args: AugmentRouteArgs
    ): Promise<AugmentRouteResponse> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!args.date) {
            return Promise.reject('Missing date');
        }
        if (!args.driverId) {
            return Promise.reject('Missing driverId');
        }

        if (
            !args.taskIds ||
            !Array.isArray(args.taskIds) ||
            !args.taskIds.length
        ) {
            return Promise.reject('Missing taskIds');
        }

        const params: SinglePlanEditArgs = {
            ...args,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}augment&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static resequenceTasks({
        clientId,
        clientPreferences,
        date,
        routeId,
        taskIds,
        taskIdsByRouteId
    }: ResequenceTasksArgs): Promise<ResequenceTasksResponse> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!date) {
            return Promise.reject('Missing date');
        }
        if (!routeId) {
            return Promise.reject('Missing routeId');
        }
        if (!taskIds || !Array.isArray(taskIds) || !taskIds.length) {
            return Promise.reject('Missing taskIds');
        }

        if (!Object.keys(taskIdsByRouteId || {}).length)
            return Promise.reject('Missing task ids breakdown by route ids');

        const params: SinglePlanEditArgs = {
            ...clientPreferences,
            date,
            routeId,
            taskIdsByRouteId,
            taskIds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}resequence&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static resequenceStops({
        clientId,
        clientPreferences,
        date,
        routeId,
        stopIds,
        stopIdsByRouteId
    }: ResequenceStopArgs): Promise<ResequenceTasksResponse> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!date) {
            return Promise.reject('Missing date');
        }
        if (!routeId) {
            return Promise.reject('Missing routeId');
        }
        if (!stopIds || !Array.isArray(stopIds) || !stopIds.length) {
            return Promise.reject('Missing stopIds');
        }

        if (!Object.keys(stopIdsByRouteId || {}).length)
            return Promise.reject('Missing stop ids breakdown by route ids');

        const params: SinglePlanEditArgs = {
            ...clientPreferences,
            date,
            routeId,
            stopIds,
            stopIdsByRouteId,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}resequence&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static optimizeRoutes({
        clientId,
        clientPreferences,
        date,
        routeIds
    }: OptimizeRoutesArgs): Promise<OptimizeRoutesResponse> {
        if (!clientId) return Promise.reject('Missing clientId');

        if (!date) return Promise.reject('Missing date');

        if (!routeIds?.length || !Array.isArray(routeIds))
            return Promise.reject('Missing routeIds');

        const params: SinglePlanEditArgs = {
            ...clientPreferences,
            date,
            routeIds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}optimize&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static reassignTasks({
        clientId,
        clientPreferences,
        date,
        taskIdsByRouteId,
        oldRouteId,
        selectedDriverId,
        selectedRouteId,
        vehicleId
    }: ReassignTasksArgs): Promise<ReassignTasksResponse> {
        if (!clientId) return Promise.reject('Missing clientId');

        if (!date) return Promise.reject('Missing date');

        if (!oldRouteId) return Promise.reject('Missing oldRouteId');

        if (!Object.keys(taskIdsByRouteId || {}).length)
            return Promise.reject('Missing task ids breakdown by route ids');

        const isNewRouteIdValid = Boolean(
            selectedRouteId && generalUtils.isValidUUID(selectedRouteId)
        );
        const newRouteId = (isNewRouteIdValid && selectedRouteId) || '';

        /**
         * Checks if driverId param is required in request.
         * If the target route id is valid, prefer rassigning by route id.
         * Otherwise, if the driver id is a uuid, use the driver id.
         * Otherwise, do not include the driver id. This handles placeholder
         * drivers such as "fake-driver-*" used for driverless routes/vehicles
         *
         * @param {boolean} targetRouteIdIsValid whether the target route id is valid
         * @param {string} maybeDriverId the driver's id in plan route level data
         * @returns the given id if it is a valid driver uuid, or undefined
         */
        const getDriverIdParam = (
            targetRouteIdIsValid: boolean,
            maybeDriverId?: string
        ) => {
            if (targetRouteIdIsValid || !maybeDriverId) return;
            if (generalUtils.isValidUUID(maybeDriverId)) return maybeDriverId;
        };

        const driverId = getDriverIdParam(isNewRouteIdValid, selectedDriverId);

        const params: SinglePlanEditArgs = {
            ...clientPreferences,
            date,
            driverId,
            vehicleId,
            newRouteId,
            oldRouteId,
            taskIdsByRouteId,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}reassign&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static addBreak({
        clientId,
        date,
        routeId,
        stopIdBeforeBreak,
        durationSeconds
    }: AddBreakArgs): Promise<AddBreakResponse> {
        if (!clientId) return Promise.reject('Missing clientId');

        if (!date) return Promise.reject('Missing date');

        if (!routeId) return Promise.reject('Missing routeId');

        if (!stopIdBeforeBreak)
            return Promise.reject('Missing stop id before break');

        if (!durationSeconds) return Promise.reject('Missing break duration');

        const params = {
            date,
            routeId,
            stopIdBeforeBreak,
            durationSeconds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}add_break&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static deleteBreak({
        clientId,
        date,
        routeId,
        breakRuleId
    }: DeleteBreakArgs): Promise<DeleteBreakResponse> {
        if (!clientId) return Promise.reject('Missing clientId');

        if (!date) return Promise.reject('Missing date');

        if (!routeId) return Promise.reject('Missing routeId');

        if (!breakRuleId) return Promise.reject('Missing breakRuleId');

        const params = {
            date,
            routeId,
            breakRuleId,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}delete_break&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static editBreak({
        clientId,
        date,
        routeId,
        breakRuleId,
        stopIdBeforeBreak,
        durationSeconds
    }: EditBreakArgs): Promise<EditBreakResponse> {
        if (!clientId) return Promise.reject('Missing clientId');

        if (!date) return Promise.reject('Missing date');

        if (!routeId) return Promise.reject('Missing routeId');

        if (!breakRuleId) return Promise.reject('Missing breakRuleId');

        // will resequence if `stopIdBeforeBreak` is provided
        // will edit duration if `durationSeconds` is provided
        if (!stopIdBeforeBreak && !durationSeconds)
            return Promise.reject(
                'Must provide either stopIdBeforeBreak or durationSeconds'
            );

        const params = {
            date,
            routeId,
            breakRuleId,
            stopIdBeforeBreak,
            durationSeconds,
            type: constants.productTypes.SCHEDULER
        };

        return axios.post(`${this.path}edit_break&accept`, params, {
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static moveRoute({
        newDate,
        newEid,
        routeDate,
        routeId
    }: MoveRouteArgs): Promise<AxiosApiResponse> {
        if (!newDate) return Promise.reject('Missing newDate');

        if (!routeDate) return Promise.reject('Missing routeDate');

        if (!routeId) return Promise.reject('Missing routeId');

        const params = {
            routeId,
            newDate,
            date: routeDate,
            type: constants.productTypes.SCHEDULER,
            ...(Boolean(newEid) && {
                newEid
            })
        };

        return axios.post(`${this.path}postpone&accept`, params);
    }

    static addNewTripToRoute({
        date,
        driverId,
        taskIdsByRouteId,
        vehicleId
    }: AddNewTripToRouteArgs): Promise<AxiosApiResponse> {
        if (!date) return Promise.reject('Missing date');

        if (!taskIdsByRouteId) {
            return Promise.reject('Missing taskIdsByRouteId');
        }

        if (!driverId && !vehicleId) {
            return Promise.reject('Missing driverId and vehicleId');
        }

        const params = {
            date,
            taskIdsByRouteId,
            type: constants.productTypes.SCHEDULER,
            ...(Boolean(driverId) && {
                driverId
            }),
            ...(Boolean(vehicleId) && {
                vehicleId
            })
        };

        return axios.post(`${this.path}construct_trip&accept`, params);
    }

    static repairPlan({ date }: RepairPlanArgs): Promise<RepairPlanResponse> {
        if (!date) return Promise.reject('Missing date');

        const params = {
            date
        };

        return axios.post(`${this.path}repair`, params);
    }
}
